import {
  Flex,
  VStack,
  Button,
  useDisclosure,
  Icon,
} from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useEditState from '../../../../../../common/src/hooks/useEditState';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import { CompanyInvestment } from '../../../../types/CompanyScanTypes';
import CompanyInvestmentTable from './CompanyInvestmentTable';
import NewCompanyInvestmentModal from './NewCompanyInvestmentModal';

interface Props {
  question: FlowsQuestion;
}

const CompanyInvestmentInputComponent: React.FC<
  React.PropsWithChildren<Props>
> = ({ question }) => {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { toBeEdited, setToBeEdited, editIndex, setEditIndex } =
    useEditState<CompanyInvestment>();

  const {
    field: { value, onBlur },
    // error,
  } = useFlowInputComponent({ question, defaultValue: [] });

  const companyInvestments = (value as CompanyInvestment[] | undefined) || [];

  const addCompanyInvestment = (companyInvestment: CompanyInvestment) => {
    onBlur({
      name: question.id,
      value: [...companyInvestments, companyInvestment],
    });
  };

  const editCompanyInvestment = (
    companyInvestment: CompanyInvestment,
    index: number,
  ) => {
    if (index > -1) {
      const newCompanyInvestments = [...companyInvestments];
      newCompanyInvestments[index] = companyInvestment;
      onBlur({ name: question.id, value: newCompanyInvestments });
    }
  };

  const handleOnRemove = (index: number) => {
    onBlur({
      name: question.id,
      value: companyInvestments.filter((_, _index) => _index !== index),
    });
  };

  const handleOnEdit = (
    companyInvestment: CompanyInvestment,
    index: number,
  ) => {
    setToBeEdited(companyInvestment);
    setEditIndex(index);
    onOpen();
  };

  const handleOnClose = () => {
    setToBeEdited(undefined);
    setEditIndex(undefined);
    onClose();
  };

  return (
    <VStack spacing="4" align="stretch">
      {isOpen && (
        <NewCompanyInvestmentModal
          companyInvestment={toBeEdited}
          isOpen={isOpen}
          onClose={handleOnClose}
          addCompanyInvestment={addCompanyInvestment}
          editCompanyInvestment={(investment) =>
            editCompanyInvestment(investment, editIndex || 0)
          }
        />
      )}
      <CompanyInvestmentTable
        companyInvestments={companyInvestments}
        onEdit={handleOnEdit}
        onRemove={handleOnRemove}
      />
      <Flex justify="flex-end">
        <Button
          onClick={onOpen}
          leftIcon={<Icon name="GeneralActionsAdd" />}
          isDisabled={question.rules.is_disabled}
        >
          {t('FLOWS_COMPANY-SCAN_COMPANY-INVESTMENT-INPUT_ADD-INVESTMENT')}
        </Button>
      </Flex>
    </VStack>
  );
};

export default CompanyInvestmentInputComponent;
